import { z } from "zod";

// Single source of truth for a store's merchant vertical — the onboarding "what do you sell?" niche.
// Consumed by the root API (updateVertical use case + the DTO below) and by crm-app's setup flow,
// so adding a niche is a one-line change here rather than three hand-synced copies.
export const STORE_VERTICALS = ['smm', 'adult-ai', 'peptides', 'nutra', 'clothing', 'others'] as const;
export type StoreVertical = (typeof STORE_VERTICALS)[number];

// Store schema with all database fields
export const StoreSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    accountId: z.string().optional(),
    credentials: z.any().nullable().optional(),
    presentmentCurrencies: z.array(z.string()).default([]),
    chargeCurrencies: z.array(z.string()).default([]),
    baseCurrency: z.string().default('-'),
    selectedPaymentFlowId: z.string().nullable().optional(),
    syncLocale: z.string().nullable().optional(),
    paypalSubscriptionEnabled: z.boolean().default(false).optional(),
    multipleSubscriptionsPerCustomerEnabled: z.boolean().default(true).optional(),
    flaggedForDeletionAt: z.date().nullable().optional(),
    lastTimeFlaggedForDeletionAt: z.date().nullable().optional(),
    experimentalFeatures: z.any().nullable().optional(),
    customThankYouUrl: z.string().nullable().optional(),
    rebillSettings: z.any().nullable().optional(),
    creditSettings: z.any().nullable().optional(),
    config: z.object({
        primaryColor: z.string().optional(),
        logoUrl: z.string().optional(),
        demoSeeding: z.object({
            products: z.enum(['pending', 'completed', 'failed']).optional(),
            paymentFlow: z.enum(['pending', 'completed', 'failed']).optional(),
            funnel: z.enum(['pending', 'completed', 'failed']).optional(),
            startedAt: z.string().optional(),
            completedAt: z.string().optional(),
            previewUrl: z.string().optional(),
        }).optional(),
        // Listed here or the settings screen erases it: this object is closed, so zod strips any
        // key it does not name, and the V2 save path reads `config` back through this DTO and
        // writes the whole column again — the round trip documented in
        // `src/app/api/crm/trpc/routers/v3/stores.ts`, which already costs cryptoPayment and
        // currencyRateOverrides on every save.
        customerPortal: z.object({
            pause: z.boolean().optional(),
            skip: z.boolean().optional(),
            reschedule: z.boolean().optional(),
            quantity: z.boolean().optional(),
            frequency: z.boolean().optional(),
            swapProduct: z.boolean().optional(),
        }).optional(),
    }).nullable().optional(),
    productsCount: z.number().default(0),
    createdAt: z.date().default(new Date()),
    updatedAt: z.date().default(new Date()),
    applyExternalBXGYDiscountOnSubscription: z.boolean().default(false),
    digitalProduct: z.boolean().default(false).optional(),
    vertical: z.enum(STORE_VERTICALS).nullable().optional().catch(null),
});

export type Store = z.infer<typeof StoreSchema>;
export const StoreDto = (object: unknown) => StoreSchema.parse(object);
